import React from 'react';
import { T, NUMERIC } from '../customer/customers.theme';
import {
  Dialog,
  DialogHeader,
  ErrorLine,
  PillField,
  PrimaryButton,
} from '../customer/CustomersUI';
import { ImageField, SelectField } from '../menu/MenuUI';
import { previewUrl, uploadMenuImage, STOCK_FOLDER } from '../menu/uploadMenuImage';
import { COPY, DEFAULT_UNITS } from './stock.copy';

/**
 * A stock item: a name, what it is counted in, and when to call it low.
 *
 * Deliberately short, because two of the fields somebody expects are not this
 * form's to give:
 *
 *   QUANTITY  is never accepted — `createStock` forces it to 0. Stock arrives
 *             through a movement, so that the shelf and the history can never
 *             disagree. The first load is a Stock In with the Opening reason.
 *
 *   COST      is the weighted average of what was actually paid, re-derived by
 *             every purchase. The server refuses to change it once the item
 *             has stock, so it is only offered while the item is empty.
 *
 * The picture is the one field the phone does NOT have a control for. The
 * model carries `imageUrl` and every screen that draws an item already reads
 * it, so the picture is added here through the same uploader the menu uses —
 * same bucket, same compression, same shape of URL — into its own
 * `Stock Images` folder. The API never sees the file; it stores a string.
 */
export default function StockItemDialog({ item, initialName, units, onClose, onSubmit }) {
  const editing = Boolean(item);
  const quantity = Number(item?.quantity || 0);

  // A business carries its own list; `assertUnit` falls back to this one, so
  // the picker has to as well or it offers a unit the server will refuse.
  const options = units && units.length ? units : DEFAULT_UNITS;

  // `initialName` is what somebody typed into the picker in the entry sheet
  // and found nothing for — so the form opens with it already filled in.
  const [name, setName] = React.useState(() => item?.name || initialName || '');
  const [unit, setUnit] = React.useState(() => item?.unit || options[0]);
  const [cost, setCost] = React.useState(() => (item ? String(item.cost ?? '') : ''));
  // A new item starts at 5. Left empty it would never be flagged, and an item
  // nobody set a level for is exactly the one that runs out unnoticed.
  const [reorder, setReorder] = React.useState(() =>
    item ? String(item.reorderLevel ?? '') : '5'
  );
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState('');

  // The two the server locks once anything is on the shelf, for exactly the
  // reasons it gives back in its error messages.
  const locked = editing && quantity !== 0;

  /* ── the picture ──────────────────────────────────────────────────────────
     `imageUrl` is what will be saved; `preview` is the local file while it is
     still going up, so the tile fills the instant something is chosen. */

  const [imageUrl, setImageUrl] = React.useState(() => item?.imageUrl || '');
  const [preview, setPreview] = React.useState('');
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress] = React.useState(0);

  // Without this the blob is held for the life of the page.
  React.useEffect(
    () => () => {
      if (preview) URL.revokeObjectURL(preview);
    },
    [preview]
  );

  const pickImage = async (file) => {
    setError('');
    setProgress(0);
    setUploading(true);

    const local = previewUrl(file);
    setPreview(local);

    try {
      const url = await uploadMenuImage(file, { onProgress: setProgress, folder: STOCK_FOLDER });
      setImageUrl(url);
    } catch (uploadError) {
      setPreview('');
      URL.revokeObjectURL(local);
      setError(uploadError.message || 'Could not upload the image');
    } finally {
      setUploading(false);
    }
  };

  const clearImage = () => {
    if (preview) URL.revokeObjectURL(preview);
    setPreview('');
    setImageUrl('');
  };

  const submit = async () => {
    // Saving mid-upload would store the item without the picture that is
    // seconds from being ready.
    if (busy || uploading) return;

    if (!name.trim()) {
      setError(COPY.nameMissing);
      return;
    }

    setBusy(true);
    setError('');

    try {
      const payload = {
        name: name.trim(),
        reorderLevel: Number(reorder) || 0,
        // Sent even when empty: removing a picture has to reach the server,
        // and `updateStock` only touches a field it was actually given.
        imageUrl: imageUrl || '',
      };

      // Sent only when they can legally change, so an untouched form can never
      // trip the server's guards.
      if (!locked) {
        payload.unit = unit;
        payload.cost = Number(cost) || 0;
      }

      await onSubmit(payload);
      onClose();
    } catch (requestError) {
      setError(requestError.message || 'Could not save');
      setBusy(false);
    }
  };

  return (
    <Dialog onClose={onClose} locked={busy}>
      <div style={{ padding: '22px 24px 24px' }}>
        <DialogHeader
          title={editing ? COPY.editItemTitle : COPY.newItemTitle}
          onClose={onClose}
          locked={busy}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 16 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 14,
              padding: 12,
              background: T.sunk,
              border: `1px solid ${T.border}`,
              borderRadius: T.radiusControl,
              fontSize: 12.5,
              color: T.inkSoft,
            }}
          >
            {editing ? (
              <span>
                <b style={{ display: 'block', color: T.ink, ...NUMERIC }}>
                  {quantity} {item.unit} on hand
                </b>
                {COPY.quantityIsHistory}
              </span>
            ) : (
              <span>
                <b style={{ display: 'block', color: T.ink }}>Starts empty</b>
                {COPY.quantityIsHistory}
              </span>
            )}
          </div>

          <PillField
            label={COPY.nameLabel}
            name="stock-name"
            value={name}
            onChange={(next) => {
              setName(next);
              setError('');
            }}
            onEnter={submit}
            placeholder={COPY.nameHint}
            disabled={busy}
            invalid={Boolean(error) && !name.trim()}
            autoFocus
          />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <SelectField
              label={COPY.unitLabel}
              name="stock-unit"
              value={unit}
              onChange={setUnit}
              disabled={busy || locked}
            >
              {options.map((entry) => (
                <option key={entry} value={entry}>
                  {entry}
                </option>
              ))}
            </SelectField>

            <PillField
              label={COPY.costLabel}
              name="stock-cost"
              type="number"
              value={locked ? String(item.cost ?? '') : cost}
              onChange={setCost}
              onEnter={submit}
              placeholder="0.00"
              disabled={busy || locked}
            />
          </div>

          {locked ? (
            <span style={{ fontSize: 12, color: T.inkFaint }}>{COPY.costIsPurchases}</span>
          ) : null}

          <PillField
            label={COPY.reorderLabel}
            name="stock-reorder"
            type="number"
            value={reorder}
            onChange={setReorder}
            onEnter={submit}
            placeholder={COPY.reorderHint}
            disabled={busy}
          />

          <span style={{ fontSize: 12, color: T.inkFaint }}>
            The Low tab uses this. Leave it empty and the item is never flagged.
          </span>

          {/* Optional. Without one, every screen falls back to the item's
              initials, which is the menu's own rule. */}
          <ImageField
            src={preview || imageUrl}
            busy={uploading}
            progress={progress}
            disabled={busy}
            onPick={pickImage}
            onClear={clearImage}
          />
        </div>

        <div style={{ paddingTop: 16 }}>
          <ErrorLine>{error}</ErrorLine>
        </div>

        <PrimaryButton
          onClick={submit}
          busy={busy}
          disabled={uploading}
          style={{ width: '100%', marginTop: error ? 12 : 20 }}
        >
          {editing ? COPY.saveChanges : COPY.addItem}
        </PrimaryButton>
      </div>
    </Dialog>
  );
}
